/**
 * PromoCardComparison Page
 *
 * Displays all 4 promotional card variants side-by-side for design selection.
 */

// Import the promo card components from AvailabilityPicker
// Variant 1: Bold & Benefit-Driven
const PromoCard1 = ({ onClose }: { onClose: () => void }) => (
  <div className="w-full mb-4 bg-gradient-to-br from-primary/10 via-cream to-cream rounded-2xl shadow-xl p-5 border-2 border-primary/30">
    <div className="flex items-start gap-4">
      <div className="text-5xl">📱</div>
      <div className="flex-1">
        <p className="text-base font-extrabold text-text-main mb-1">
          Skip the back-and-forth
        </p>
        <p className="text-xs text-text-light font-bold mb-3">
          Seamless scheduling, zero friction
        </p>
        <button className="bg-primary hover:bg-primary-dark text-white font-extrabold py-2.5 px-5 rounded-xl transition-all active:scale-95 shadow-lg shadow-primary/25">
          Get CatchUp
        </button>
      </div>
      <button
        onClick={onClose}
        className="text-text-light hover:text-text-main font-bold text-xl leading-none"
        aria-label="Close promotional card"
      >
        ×
      </button>
    </div>
  </div>
);

// Variant 2: Minimal & Elegant
const PromoCard2 = ({ onClose }: { onClose: () => void }) => (
  <div className="w-full mb-4 bg-white rounded-2xl shadow-lg p-6 border border-cream-dark">
    <div className="flex items-start justify-between">
      <div className="flex-1 space-y-3">
        <p className="text-lg font-extrabold text-text-main tracking-tight">
          Never send another link
        </p>
        <p className="text-sm text-text-light font-semibold">
          Seamless scheduling, zero friction
        </p>
        <button className="text-sm bg-text-main hover:bg-text-light text-cream font-bold py-2 px-5 rounded-lg transition-colors mt-2">
          Download CatchUp
        </button>
      </div>
      <button
        onClick={onClose}
        className="text-text-light hover:text-text-main font-bold text-xl leading-none ml-4"
        aria-label="Close promotional card"
      >
        ×
      </button>
    </div>
  </div>
);

// Variant 3: Social Proof
const PromoCard3 = ({ onClose }: { onClose: () => void }) => (
  <div className="w-full mb-4 bg-white rounded-2xl shadow-xl p-5 border border-cream-dark ring-2 ring-primary/10">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-yellow-500 text-lg">★★★★★</div>
          <span className="text-xs text-text-light font-bold">4.9/5</span>
        </div>
        <p className="text-sm font-extrabold text-text-main mb-1">
          Join 10,000+ easy schedulers
        </p>
        <p className="text-xs text-text-light font-semibold mb-3">
          Stop playing calendar ping-pong. Get CatchUp.
        </p>
        <button className="text-xs bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded-xl transition-colors">
          Join the community
        </button>
      </div>
      <button
        onClick={onClose}
        className="text-text-light hover:text-text-main font-bold text-xl leading-none ml-4"
        aria-label="Close promotional card"
      >
        ×
      </button>
    </div>
  </div>
);

// Variant 4: Playful & Conversational
const PromoCard4 = ({ onClose }: { onClose: () => void }) => (
  <div className="w-full mb-4 bg-gradient-to-br from-cream via-white to-cream rounded-2xl shadow-lg p-5 border-2 border-cream-dark">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">✨</span>
          <p className="text-sm font-extrabold text-text-main">
            That was easy, right?
          </p>
        </div>
        <p className="text-xs text-text-light font-bold mb-3">
          Make this even easier next time with 1-tap replies
        </p>
        <button className="text-xs bg-primary/90 hover:bg-primary text-white font-bold py-2.5 px-4 rounded-xl transition-all flex items-center gap-1">
          Get the app <span>🙌</span>
        </button>
      </div>
      <button
        onClick={onClose}
        className="text-text-light hover:text-text-main font-bold text-xl leading-none ml-4"
        aria-label="Close promotional card"
      >
        ×
      </button>
    </div>
  </div>
);

export default function PromoCardComparison() {
  return (
    <div className="font-display antialiased text-text-main min-h-screen bg-cream">
      {/* Header */}
      <header className="w-full bg-cream pt-12 pb-6 px-6">
        <div className="max-w-2xl mx-auto space-y-2">
          <h1 className="text-3xl font-extrabold tracking-tight text-text-main">
            Choose Your Promo Card Design
          </h1>
          <p className="text-base text-text-light font-semibold">
            Select your favorite promotional card variant for the availability picker
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="w-full px-6 pb-12">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Variant 1 */}
          <section>
            <div className="mb-3">
              <h2 className="text-sm font-bold text-text-main mb-1">
                Variant 1: Bold & Benefit-Driven
              </h2>
              <p className="text-xs text-text-light font-semibold">
                Gradient background, clear benefit statement, strong CTA
              </p>
            </div>
            <PromoCard1 onClose={() => {}} />
          </section>

          {/* Variant 2 */}
          <section>
            <div className="mb-3">
              <h2 className="text-sm font-bold text-text-main mb-1">
                Variant 2: Minimal & Elegant
              </h2>
              <p className="text-xs text-text-light font-semibold">
                Clean white background, understated elegance, focus on simplicity
              </p>
            </div>
            <PromoCard2 onClose={() => {}} />
          </section>

          {/* Variant 3 */}
          <section>
            <div className="mb-3">
              <h2 className="text-sm font-bold text-text-main mb-1">
                Variant 3: Social Proof
              </h2>
              <p className="text-xs text-text-light font-semibold">
                5-star rating, community validation, credibility-focused
              </p>
            </div>
            <PromoCard3 onClose={() => {}} />
          </section>

          {/* Variant 4 */}
          <section>
            <div className="mb-3">
              <h2 className="text-sm font-bold text-text-main mb-1">
                Variant 4: Playful & Conversational
              </h2>
              <p className="text-xs text-text-light font-semibold">
                Friendly emoji usage, conversational tone, warm and approachable
              </p>
            </div>
            <PromoCard4 onClose={() => {}} />
          </section>
        </div>
      </main>
    </div>
  );
}
